package com.playoffpulse.frontend.ui

import com.playoffpulse.shared.logic.PlayoffTextFormatter
import com.playoffpulse.shared.logic.PlayoffObjectiveResolver
import com.playoffpulse.shared.model.DraftWatchResponse
import com.playoffpulse.shared.model.MagicStatus
import com.playoffpulse.shared.model.MagicSummary

object ShareFormatter {
    fun pts(points: Int): String = PlayoffTextFormatter.formatPts(points)

    fun current(points: Int, maxPossiblePoints: Int): String = "${pts(points)} (${maxPossiblePoints} max)"

    fun requirement(points: Int): String = pts(points)

    fun requirement(summary: MagicSummary): String = requirement(summary.magicPointsToClinch)

    fun requirement(
        points: Int,
        lowestWins: Int?,
        asterisk: Boolean,
    ): String {
        return pts(points)
    }

    fun ptsWins(points: Int): String = PlayoffTextFormatter.formatPtsWins(points)

    fun shareText(
        summary: MagicSummary,
        draftWatch: DraftWatchResponse? = null,
    ): String {
        if (draftWatch?.active == true) {
            return buildString {
                append("Playoff Pulse | ")
                append(summary.team.displayName)
                append(" -> ")
                append(draftWatch.currentDraftTag ?: "Draft Watch")
                append(" | Current: ")
                append(current(summary.currentPoints, summary.maxPossiblePoints))
                append(" | Best possible: ")
                append(draftWatch.bestPossibleDraftSlotLabel ?: "—")
                append(" | Worst possible: ")
                append(draftWatch.worstPossibleDraftSlotLabel ?: "—")
                append(" | Next better tag: ")
                append(draftWatch.nextBetterDraftTag ?: "1-3")
            }
        }

        val statusLabel = statusLabel(summary)

        return buildString {
            append("Playoff Pulse | ")
            append(summary.team.displayName)
            append(" -> ")
            append(statusLabel)
            append(" | Current: ")
            append(current(summary.currentPoints, summary.maxPossiblePoints))
            append(" | To clinch any seat: ")
            append(requirement(summary))
            append(" | Clinch target: ")
            append(pts(summary.clinchTargetPoints))
            append(" | Cut line: ")
            append(pts(summary.conferenceCutLinePoints))
        }
    }

    fun statusLabel(summary: MagicSummary): String = when (summary.status) {
        MagicStatus.CLINCHED -> when {
            summary.nextClinchTargetSeatCode != null -> "${summary.nextClinchTargetSeatCode} Next"
            summary.highestClinchedSeatCode != null -> "${summary.highestClinchedSeatCode} Clinched"
            else -> "Berth Clinched"
        }

        MagicStatus.IN_POSITION ->
            if (PlayoffObjectiveResolver.highestHeldSeat(summary) != null) {
                "HOLDS ${PlayoffObjectiveResolver.highestHeldSeat(summary)}"
            } else {
                "IN POSITION"
            }

        MagicStatus.ELIMINATED -> "ELIMINATED"
        MagicStatus.CHASING -> "CHASING"
        MagicStatus.CONTROLS_DESTINY -> "CONTROLS DESTINY"
    }

    fun nextContendingGoal(summary: MagicSummary): String? {
        val heldSeat = PlayoffObjectiveResolver.highestHeldSeat(summary)
        val nextGoal = PlayoffObjectiveResolver.nextGoalCode(summary)
        return when {
            summary.status == MagicStatus.CLINCHED && heldSeat != null ->
                "Currently holding $heldSeat"
            summary.status == MagicStatus.CLINCHED && summary.nextClinchTargetSeatCode != null ->
                "Contending for ${summary.nextClinchTargetSeatCode}"
            nextGoal != null ->
                "Contending for $nextGoal"
            else -> null
        }
    }
}
